import React from 'react'
import { Tooltip } from 'antd'

/**
 * Whether the number on screen is known to leave out an edit in progress.
 *
 * Not another thing an estimate can be: it qualifies whichever state the estimate is already in, and
 * the estimate it qualifies is the one that was correct before the reader opened the editor.
 */
export type Staleness = 'current' | 'pending-edit'

/** Reads the builder's open-editor count as the staleness it implies for anything derived from the document. */
export function stalenessOf(editingCount: number): Staleness {
    return editingCount > 0 ? 'pending-edit' : 'current'
}

/**
 * Marks an estimate the reader should not read as describing what is on screen.
 *
 * Dimmed and explained rather than replaced: the number is still the best answer available for the
 * segment as it stands committed, and blanking it during every edit would lose that and make the
 * footer flicker between a number and nothing. Renders its children untouched when nothing is being
 * edited, so the ordinary case carries no wrapper of consequence.
 *
 * Styled inline for the same reason the builder's own list footer is: this renders into a footer slot
 * whose stylesheet these components do not own.
 */
export function PendingEdit({ staleness, children }: { staleness: Staleness; children: React.ReactNode }) {
    if (staleness === 'current') return <>{children}</>

    return (
        <Tooltip title="This does not include the condition you are editing. It updates when you save.">
            <span className="reach-pending-edit" style={{ opacity: 0.45 }}>
                {children}
            </span>
        </Tooltip>
    )
}
